/** @module Status page: shows readiness of database, migrations, Valkey and workers. */
import { useTranslation } from "react-i18next";

import { useReadiness } from "@/features/status/use-readiness";

function StatusDot({ ok, label }: { ok: boolean; label: string }) {
  // Status is never conveyed by colour alone (WCAG 2.2 AA).
  return (
    <span className="inline-flex items-center gap-1 text-sm">
      <span aria-hidden="true" style={{ color: ok ? "var(--color-ok)" : "var(--color-bad)" }}>
        {ok ? "✓" : "✗"}
      </span>
      <span>{label}</span>
    </span>
  );
}

export function StatusPage() {
  const { t } = useTranslation();
  const { data, status, refetch } = useReadiness();

  if (status === "pending") {
    return <p role="status">{t("status.loading")}</p>;
  }

  if (status === "error") {
    return (
      <div role="alert" className="space-y-3">
        <p>{t("status.error")}</p>
        <button
          type="button"
          className="rounded border border-(--color-line) px-3 py-2"
          onClick={() => void refetch()}
        >
          {t("status.retry")}
        </button>
      </div>
    );
  }

  const ready = data.status === "ok";

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-base font-semibold">{t("status.title")}</h2>
        <p className="text-sm text-(--color-ink-muted)">{t("status.description")}</p>
      </div>

      <p className="font-medium">
        <StatusDot ok={ready} label={ready ? t("status.ready") : t("status.notReady")} />{" "}
        <span className="text-sm text-(--color-ink-muted)">
          {t("status.version", { version: data.version })}
        </span>
      </p>

      <ul className="divide-y divide-(--color-line) rounded border border-(--color-line)">
        {Object.entries(data.checks).map(([name, check]) => (
          <li key={name} className="flex items-center justify-between gap-4 px-3 py-2">
            <span>{t(`status.checks.${name}`, { defaultValue: name })}</span>
            <StatusDot
              ok={check.status === "ok"}
              label={check.detail ?? t(`status.check.${check.status}`)}
            />
          </li>
        ))}
      </ul>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">{t("status.workers.title")}</h3>
        <ul className="space-y-1">
          {Object.entries(data.workers).map(([queue, alive]) => (
            <li key={queue}>
              <StatusDot
                ok={alive}
                label={`${queue} – ${alive ? t("status.workers.alive") : t("status.workers.missing")}`}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
